// Browser Supabase client. Import this everywhere in client code:
//   import { supabase } from "@/integrations/supabase/browser-client";
//
// It targets whichever instance the current environment resolves to:
//   - Lovable preview  -> Lovable Cloud, with the Lovable preview auth broker.
//   - AWS production   -> self-hosted Supabase, with normal browser storage.
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { requirePublicSupabaseConfig } from "./runtime-config";
import { selfHostedFetch, supabaseStorageKey } from "./self-hosted";
import { brokeredPreviewStorage } from "./previewAuthStorage";

type BrowserClient = SupabaseClient<any, "public", "public", any, any>;

function createBrowserClient(): BrowserClient {
  const { url, publishableKey, source } = requirePublicSupabaseConfig();

  const storage =
    typeof window === "undefined"
      ? undefined
      : source === "lovable-cloud"
        ? brokeredPreviewStorage()
        : window.localStorage;

  return createClient(url, publishableKey, {
    global: {
      fetch: selfHostedFetch(publishableKey),
    },
    auth: {
      storage,
      storageKey: supabaseStorageKey(source),
      persistSession: true,
      autoRefreshToken: true,
      // OAuth returns to the app with ?code=...; supabase-js exchanges it for a
      // session automatically when detectSessionInUrl is on.
      detectSessionInUrl: true,
      flowType: "pkce",
    },
  }) as BrowserClient;
}

let _client: BrowserClient | undefined;

export const supabase = new Proxy({} as BrowserClient, {
  get(_, prop, receiver) {
    if (!_client) _client = createBrowserClient();
    return Reflect.get(_client, prop, receiver);
  },
});
